import React, { useEffect, useMemo, useState } from 'react';
import {
  Divider,
  Grid,
  Header,
  Label,
  Loader,
  Message,
  Modal,
  Segment,
  SemanticCOLORS
} from 'semantic-ui-react';

import Button from '../../../components/Button';
import { dangerNotification, successNotification } from '../../../services/notification';
import {
  getStandaloneChargeBillingEstate,
  retryStandaloneChargeBiometry,
  StandaloneChargeBillingEstateDetailDTO
} from '../../../services/standalone-charges';
import { billingEstateStatusLabelMap } from '../constants';
import { BillingEstateItem } from '../types';
import FieldBlock from './FieldBlock';
import {
  currencyFormatter,
  formatDateTime
} from '../utils/activationDetailsHelpers';

import ActivationRegistrationSections from './ActivationRegistrationSections';
import BiometryStatusCard from './BiometryStatusCard';
import EditBankAccountModal from './EditBankAccountModal';
import RecipientAttemptsTimeline from './RecipientAttemptsTimeline';
import RetryBiometryConfirmModal from './RetryBiometryConfirmModal';

type Props = {
  open: boolean;
  estateId: string | null;
  onClose: () => void;
  onShareBiometry?: (estate: BillingEstateItem) => void;
  onManageRegistration?: (estateId: string) => void;
  onViewCharges?: (estateId: string) => void;
  onRetrySuccess?: () => void;
  canEditBankAccount?: boolean;
};

const billingEstateStatusColor: Record<string, string> = {
  ACTIVE: 'green',
  BIOMETRY_PENDING: 'yellow',
  BIOMETRY_REJECTED: 'red',
  SUPPORT_REQUIRED: 'orange',
  REGISTRATION_INCOMPLETE: 'orange',
  REGISTRATION_PENDING: 'grey'
};

const mapDetailToBillingEstateItem = (
  detail: StandaloneChargeBillingEstateDetailDTO
): BillingEstateItem => ({
  estateId: detail.estateId,
  estateName: detail.estateName,
  activatedAt: detail.activatedAt,
  activeCollaboratorsCount: detail.activeCollaboratorsCount,
  paidChargesCount: detail.paidChargesCount,
  totalMovedAmount: detail.totalMovedAmount,
  ladoBomRevenueAmount: detail.ladoBomRevenueAmount,
  status: detail.status,
  kycToken: detail.activation?.kycToken
});

const EstateBillingDetailsModal: React.FC<Props> = ({
  open,
  estateId,
  onClose,
  onShareBiometry,
  onManageRegistration,
  onViewCharges,
  onRetrySuccess,
  canEditBankAccount = true
}) => {
  const [detail, setDetail] = useState<StandaloneChargeBillingEstateDetailDTO | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryModalOpen, setRetryModalOpen] = useState(false);
  const [retryLoading, setRetryLoading] = useState(false);
  const [editBankAccountOpen, setEditBankAccountOpen] = useState(false);

  const loadDetail = () => {
    if (!estateId) return Promise.resolve();

    setLoading(true);
    setError(null);

    return getStandaloneChargeBillingEstate(estateId)
      .then(data => setDetail(data))
      .catch(() => {
        setError('Não foi possível carregar os detalhes da imobiliária.');
        dangerNotification('Erro', 'Não foi possível carregar os detalhes da imobiliária.');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open || !estateId) {
      setDetail(null);
      setError(null);
      setRetryModalOpen(false);
      return;
    }

    loadDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, estateId]);

  const activationSource = useMemo(() => {
    if (!detail?.activation) return null;

    return {
      recipientType: detail.activation.recipientType,
      registerInformation: detail.activation.registerInformation,
      defaultBankAccount: detail.activation.defaultBankAccount,
      pagarmeRecipientId: detail.activation.pagarmeRecipientId,
      lastSyncAt: detail.activation.lastSyncAt,
      lastSyncStatus: detail.activation.lastSyncStatus,
      lastSyncError: detail.activation.lastSyncError,
      completedAt: detail.activation.completedAt,
      createdAt: detail.activation.createdAt,
      updatedAt: detail.activation.updatedAt
    };
  }, [detail]);

  const estateItem = detail ? mapDetailToBillingEstateItem(detail) : null;
  const activationMeta = detail?.activation;

  const showShareBiometry =
    detail?.status === 'BIOMETRY_PENDING' && onShareBiometry && estateItem;
  const showManageRegistration =
    (detail?.status === 'REGISTRATION_PENDING' ||
      detail?.status === 'REGISTRATION_INCOMPLETE') &&
    onManageRegistration &&
    detail?.estateId;
  const showRetryBiometry =
    detail?.status === 'BIOMETRY_REJECTED' && activationMeta?.canRetryBiometry;
  const showViewCharges =
    detail?.status === 'ACTIVE' && onViewCharges && detail?.estateId;
  const showEditBankAccount =
    canEditBankAccount &&
    detail?.status === 'ACTIVE' &&
    Boolean(detail?.activation?.pagarmeRecipientId);

  const handleRetryBiometry = async () => {
    if (!estateId) return;
    setRetryLoading(true);
    try {
      await retryStandaloneChargeBiometry(estateId);
      setRetryModalOpen(false);
      successNotification(
        'Sucesso',
        'Novo cadastro criado. Compartilhe a biometria com a imobiliária.'
      );
      await loadDetail();
      onRetrySuccess?.();
    } catch (err) {
      dangerNotification(
        'Erro',
        err instanceof Error
          ? err.message
          : 'Não foi possível iniciar uma nova tentativa de biometria.'
      );
    } finally {
      setRetryLoading(false);
    }
  };

  return (
    <>
      <Modal open={open} onClose={onClose} size="large" closeIcon>
        <Modal.Header>Detalhes da imobiliária</Modal.Header>
        <Modal.Content scrolling>
          {loading && (
            <Segment basic style={{ minHeight: 160 }}>
              <Loader active inline="centered">
                Carregando…
              </Loader>
            </Segment>
          )}
          {!loading && error && <Message negative>{error}</Message>}
          {!loading && !error && detail && (
            <>
              <Header as="h4" style={{ marginTop: 0, marginBottom: 16 }}>
                Resumo
              </Header>
              <Grid stackable columns={3}>
                <Grid.Column width={6}>
                  <FieldBlock label="Nome">{detail.estateName}</FieldBlock>
                </Grid.Column>
                <Grid.Column width={4}>
                  <FieldBlock label="Status">
                    <Label
                      color={
                        (billingEstateStatusColor[detail.status] || 'grey') as SemanticCOLORS
                      }
                      size="small"
                    >
                      {billingEstateStatusLabelMap[detail.status] || detail.status}
                    </Label>
                  </FieldBlock>
                </Grid.Column>
                <Grid.Column width={6}>
                  <FieldBlock label="Data de ativação">
                    {formatDateTime(detail.activatedAt)}
                  </FieldBlock>
                </Grid.Column>
                <Grid.Column width={4}>
                  <FieldBlock label="Colaboradores ativos">
                    {detail.activeCollaboratorsCount}
                  </FieldBlock>
                </Grid.Column>
                <Grid.Column width={4}>
                  <FieldBlock label="Cobranças realizadas">{detail.paidChargesCount}</FieldBlock>
                </Grid.Column>
                <Grid.Column width={4}>
                  <FieldBlock label="Total movimentado">
                    {currencyFormatter.format(detail.totalMovedAmount || 0)}
                  </FieldBlock>
                </Grid.Column>
                <Grid.Column width={4}>
                  <FieldBlock label="Receita LadoBom">
                    {currencyFormatter.format(detail.ladoBomRevenueAmount || 0)}
                  </FieldBlock>
                </Grid.Column>
                <Grid.Column width={8}>
                  <FieldBlock label="Emissão de cobranças">
                    {detail.standaloneChargesEnabled ? 'Liberada' : 'Bloqueada'}
                  </FieldBlock>
                </Grid.Column>
              </Grid>

              {detail.status === 'BIOMETRY_REJECTED' && (
                <>
                  <Divider section />
                  <BiometryStatusCard
                    variant={activationMeta?.canRetryBiometry ? 'rejected' : 'limitReached'}
                    meta={activationMeta}
                  />
                </>
              )}

              {detail.status === 'SUPPORT_REQUIRED' && (
                <>
                  <Divider section />
                  <BiometryStatusCard variant="support" meta={activationMeta} />
                </>
              )}

              {detail.activation && activationSource ? (
                <>
                  <Divider section />
                  <ActivationRegistrationSections source={activationSource} />
                </>
              ) : (
                <>
                  <Divider section />
                  <Message info>
                    <p style={{ margin: 0 }}>
                      Cadastro ainda não iniciado. Conclua o onboarding para liberar cobranças.
                    </p>
                  </Message>
                </>
              )}

              {activationMeta?.recipientAttempts && activationMeta.recipientAttempts.length > 0 && (
                <>
                  <Divider section />
                  <Header as="h4" style={{ marginBottom: 12 }}>
                    Histórico de tentativas
                  </Header>
                  <RecipientAttemptsTimeline attempts={activationMeta.recipientAttempts} />
                </>
              )}
            </>
          )}
        </Modal.Content>
        <Modal.Actions>
          {showShareBiometry && estateItem && onShareBiometry && (
            <Button
              type="button"
              text="Compartilhar biometria"
              onClick={() => onShareBiometry(estateItem)}
            />
          )}
          {showRetryBiometry && (
            <Button
              type="button"
              text="Reprocessar biometria"
              onClick={() => setRetryModalOpen(true)}
            />
          )}
          {showManageRegistration && onManageRegistration && (
            <Button
              type="button"
              text="Gerenciar cadastro"
              onClick={() => onManageRegistration(detail!.estateId)}
            />
          )}
          {showViewCharges && onViewCharges && (
            <Button
              type="button"
              text="Ver cobranças"
              onClick={() => onViewCharges(detail!.estateId)}
            />
          )}
          {showEditBankAccount && (
            <Button
              type="button"
              text="Alterar conta bancária"
              onClick={() => setEditBankAccountOpen(true)}
            />
          )}
          <Button type="button" onClick={onClose}>
            Fechar
          </Button>
        </Modal.Actions>
      </Modal>

      <EditBankAccountModal
        open={editBankAccountOpen}
        mode="estate"
        estateId={estateId || undefined}
        source={activationSource}
        onClose={() => setEditBankAccountOpen(false)}
        onSuccess={async () => {
          await loadDetail();
          onRetrySuccess?.();
        }}
      />

      <RetryBiometryConfirmModal
        open={retryModalOpen}
        loading={retryLoading}
        attemptCount={activationMeta?.recipientAttemptCount}
        maxAttempts={activationMeta?.maxRecipientAttempts}
        subjectLabel="recebedor da imobiliária"
        onConfirm={handleRetryBiometry}
        onClose={() => setRetryModalOpen(false)}
      />
    </>
  );
};

export default EstateBillingDetailsModal;
